import { FieldMergeFunction, FieldPolicy, FieldReadFunction } from '@apollo/client/cache/inmemory/policies';
import { IPageInfo } from '../graphql/schema/p4types';

/**
 * Rough estimation of a typical list - May be additional fields not listed here.
 */
type IP4List = {
  edges?: Array<{ node?: any }>;
  pageInfo?: IPageInfo;
  genericCount?: number;
};

const p4ListMergeFunction: FieldMergeFunction<IP4List> = (existing, incoming, { canRead, variables }) => {
  if (incoming?.edges && incoming.pageInfo && variables?.first && incoming.edges.length > variables.first) {
    //This is an invalid state for the incoming data - There should be not more rows in the incoming data than the page size (First).
    //Since it is unclear whether to use existing or incoming in this scenario, instead returning undefined and forcing all current pages to be refetched.
    return undefined;
  }
  let mergedEdges: Array<any>;
  if (!incoming?.pageInfo || !incoming?.edges) {
    //Ignore existing data and simply return incoming if we have no pageInfo or an otherwise invalid/unexpected object (Non-paged query - The GraphQL definition is missing the required fields).
    return incoming;
  } else if (!existing?.edges || existing?.genericCount !== incoming?.genericCount) {
    //If this is the first page returned, or the count has changed (Implying the data has changed in some way, such as an order changing status, or was added/deleted).
    //Treat existing as an empty array, but still "merge" so the data goes into the correct place, if loaded out-of-order.
    // (I.e. Avoid setting Page 2+ as the first 30 rows).
    mergedEdges = [];
  } else {
    //Otherwise - We can merge into the existing edges.
    mergedEdges = existing.edges.slice(0);
  }
  const endCursor = parseInt(incoming.pageInfo.endCursor ?? '0', 10);
  //Use the startCursor where possible, fall back to the endCursor minus # of edges otherwise.
  //Note - Due to a bug where apollo gives an incoming page with multiple pages of content, ensure this can't be negative.
  const startCursor = incoming.pageInfo.startCursor
    ? parseInt(incoming.pageInfo.startCursor, 10) - 1
    : Math.max(endCursor - incoming.edges.length, 0);
  const max = mergedEdges.length > endCursor ? mergedEdges.length : endCursor;
  for (let i = 0; i < max; i++) {
    if (i >= startCursor && i < endCursor) {
      mergedEdges[i] = incoming.edges[i - startCursor];
    } else if (!canRead(mergedEdges[i])) {
      mergedEdges[i] = null; //Empty entries replaced with null, or apollo cuts them and makes a mess of the paging...
    }
  }

  return {
    ...incoming,
    edges: mergedEdges,
  };
};

const p4ListPolicy: FieldPolicy = {
  keyArgs: ['filters', 'orderBy', 'orderDirection', 'aggregateCriteria', ['sumColumns', 'groupColumns']],
  merge: p4ListMergeFunction,
};

export const buildTypePolicies = (schema: {
  querySchema: { fields: any };
}): {
  [fieldName: string]: FieldPolicy<any> | FieldReadFunction<any>;
} => {
  const fieldsWithPaging: Array<string> = Object.keys(schema.querySchema.fields).filter((fieldKey) => {
    const fieldDefinition = schema.querySchema.fields[fieldKey];
    return fieldDefinition.args?.after && fieldDefinition.args?.first;
  });

  const fieldPolicies: {
    [fieldName: string]: FieldPolicy | FieldReadFunction;
  } = {};
  fieldsWithPaging.forEach((field) => {
    fieldPolicies[field] = p4ListPolicy;
  });
  return fieldPolicies;
};
